import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { workOrderProgress, STATUS_META } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import WorkOrderFormDialog from "@/components/WorkOrderFormDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Wrench, ArrowRight, Search } from "lucide-react";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "ready", label: "Ready" },
  { value: "completed", label: "Completed" },
];

const VALID_FILTERS = ["all", "new", "in_progress", "ready", "completed"];

export default function WorkOrders() {
  const [searchParams] = useSearchParams();
  const initialFilter = VALID_FILTERS.includes(searchParams.get("status"))
    ? searchParams.get("status")
    : "all";
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState({});
  const [vehicles, setVehicles] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  // React to URL changes (e.g., navigating from dashboard tile to a specific filter)
  useEffect(() => {
    const s = searchParams.get("status");
    if (VALID_FILTERS.includes(s)) setFilter(s);
  }, [searchParams]);

  const load = async () => {
    setLoading(true);
    const [o, c, v] = await Promise.all([
      api.get("/work-orders"),
      api.get("/customers"),
      api.get("/vehicles"),
    ]);
    setOrders(o.data);
    setCustomers(Object.fromEntries(c.data.map((x) => [x.id, x])));
    setVehicles(Object.fromEntries(v.data.map((x) => [x.id, x])));
    setLoading(false);
  };

  const changeStatus = async (orderId, status) => {
    try {
      await api.patch(`/work-orders/${orderId}/status`, { status });
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status } : o))
      );
      toast.success(`Status → ${STATUS_META[status].label}`);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const q = query.trim().toLowerCase();
  const searched = q
    ? orders.filter((o) => {
        const v = vehicles[o.vehicle_id];
        const c = customers[o.customer_id];
        return (
          (o.order_number || "").toLowerCase().includes(q) ||
          (v?.license_plate || "").toLowerCase().includes(q) ||
          (v ? `${v.make || ""} ${v.model || ""}`.toLowerCase() : "").includes(q) ||
          (c?.name || "").toLowerCase().includes(q) ||
          (o.complaint || "").toLowerCase().includes(q)
        );
      })
    : orders;
  const filtered = filter === "all" ? searched : searched.filter((o) => o.status === filter);

  return (
    <div>
      <PageHeader
        testid="work-orders-header"
        title="Repair Orders"
        subtitle="From drop-off to pickup — track every repair in the shop."
        actions={
          <>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-muted" />
              <Input
                data-testid="wo-search-input"
                placeholder="Search plate, order #, owner..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 w-72 rounded-lg border-2 border-warm bg-white font-mono uppercase placeholder:normal-case placeholder:font-sans"
              />
            </div>
            <Button
              data-testid="new-work-order-btn"
              onClick={() => setDialogOpen(true)}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl font-medium"
            >
              <Plus className="w-4 h-4 mr-2" /> New Repair Order
            </Button>
          </>
        }
      />

      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList className="bg-[#F3F2EE] p-1 rounded-xl" data-testid="wo-filter-tabs">
          <TabsTrigger value="all" data-testid="filter-all" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-card">
            All ({searched.length})
          </TabsTrigger>
          <TabsTrigger value="new" data-testid="filter-new" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-card">
            New ({searched.filter((o) => o.status === "new").length})
          </TabsTrigger>
          <TabsTrigger value="in_progress" data-testid="filter-in-progress" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-card">
            In Progress ({searched.filter((o) => o.status === "in_progress").length})
          </TabsTrigger>
          <TabsTrigger value="ready" data-testid="filter-ready" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-card">
            Ready ({searched.filter((o) => o.status === "ready").length})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="filter-completed" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-card">
            Completed ({searched.filter((o) => o.status === "completed").length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <Card className="p-10 text-center text-slate-muted rounded-2xl border-warm">Loading…</Card>
      ) : orders.length === 0 ? (
        <EmptyState
          testid="wo-empty"
          image="https://images.unsplash.com/photo-1596986952526-3be237187071?crop=entropy&cs=srgb&fm=jpg&w=400&q=70"
          title="No repair orders yet"
          description="Create your first repair order to start tracking jobs."
          action={
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
              data-testid="empty-new-wo"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Repair Order
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-slate-muted rounded-2xl border-warm" data-testid="wo-no-matches">
          {q ? `No repair orders match "${query}".` : "No orders in this status."}
        </Card>
      ) : (
        <Card className="rounded-2xl border-warm shadow-card overflow-hidden" data-testid="work-orders-table">
          <Table>
            <TableHeader className="bg-[#F3F2EE]">
              <TableRow>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Order #</TableHead>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Vehicle Reg #</TableHead>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Vehicle</TableHead>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Complaint</TableHead>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Progress</TableHead>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((wo) => {
                const p = workOrderProgress(wo);
                const c = customers[wo.customer_id];
                const v = vehicles[wo.vehicle_id];
                const meta = STATUS_META[wo.status] || STATUS_META.new;
                return (
                  <TableRow key={wo.id} data-testid={`wo-row-${wo.order_number}`} className="hover:bg-[#FAF9F6]">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#F3F2EE] flex items-center justify-center text-[#3D405B]">
                          <Wrench className="w-4 h-4" />
                        </div>
                        <span className="font-mono text-sm font-semibold text-[#2B2D42]">
                          {wo.order_number}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {v?.license_plate ? (
                        <div>
                          <span className="inline-block font-mono text-sm font-semibold text-[#1A1A1A] bg-[#F4D35E]/30 border border-[#E5A82E]/40 rounded-md px-2 py-0.5">
                            {v.license_plate.toUpperCase()}
                          </span>
                          <div className="text-xs text-slate-muted mt-0.5">
                            {c?.name || "—"}
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-muted">{c?.name || "—"}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-muted">
                      {v ? `${v.year || ""} ${v.make} ${v.model}`.trim() : "—"}
                    </TableCell>
                    <TableCell className="text-slate-muted max-w-xs truncate">
                      {wo.complaint || "—"}
                    </TableCell>
                    <TableCell>
                      {p.total === 0 ? (
                        <span className="text-xs text-slate-muted italic">No tasks</span>
                      ) : (
                        <div className="flex items-center gap-2 min-w-[120px]">
                          <div className="flex-1 h-1.5 rounded-full bg-[#EAE8E3] overflow-hidden">
                            <div
                              className="h-full bg-[#81B29A]"
                              style={{ width: `${p.pct}%` }}
                            />
                          </div>
                          <span className="text-xs font-mono text-[#2B2D42] w-12 text-right">
                            {p.done}/{p.total}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={wo.status}
                        onValueChange={(val) => changeStatus(wo.id, val)}
                      >
                        <SelectTrigger
                          data-testid={`row-status-select-${wo.order_number}`}
                          className={`h-auto py-1 px-2.5 rounded-full text-xs font-semibold uppercase tracking-wider border w-auto gap-1.5 focus:ring-2 focus:ring-[#E5A82E] [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-70 ${meta.cls}`}
                        >
                          <span>{meta.label}</span>
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          {STATUSES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/work-orders/${wo.id}`} data-testid={`open-wo-${wo.order_number}`}>
                        <Button variant="ghost" size="sm" className="rounded-lg text-[#3D405B]">
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <WorkOrderFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={null}
        onSaved={load}
      />
    </div>
  );
}
